import {
  Button,
  Card,
  Center,
  CloseButton,
  Dialog,
  Flex,
  Heading,
  HStack,
  Icon,
  Portal,
  Table,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { LuClock, LuMapPin } from "react-icons/lu";
import { TbCalendarTime } from "react-icons/tb";
import { useNavigate } from "react-router-dom";
import { PAYMENT_CATEGORIES } from "../../constants/paymentConstants";
import { useAuth } from "../../contexts/AuthContext";
import { useGetEventRegistrationQuery } from "../../features/eventApi";
import { useGetPaymentByIdQuery } from "../../features/paymentApi";
import { useMultipleSslPaymentMutation } from "../../features/sslPaymentApi";
import { calculateAge } from "../../utils/claculateAge";
import { formatTime } from "../../utils/formatTime";
import { getFee } from "./EventRegistrationv2";

interface PaymentTypeDialogProps {
  paymentId: string;
  transactionId?: string;
  open: boolean;
  setOpen: (open: boolean) => void;
  eventData?: {
    event_name: string;
    event_date: string;
    event_time: string;
    event_location: string;
    last_payment_date: string;
    member_fee: string;
    child_fee: string;
    adult_fee: string;
    driver_fee: string;
    guest_fee: string;
  };
  eventRegistrationId: string;
}

const EventRegistrationPaymentDialog = ({
  open,
  setOpen,
  paymentId,
  eventData,
  eventRegistrationId,
}: // data: registrationData,
PaymentTypeDialogProps) => {
  const [value, setValue] = useState<string | null>(null);
  const navigate = useNavigate();

  const { roles } = useAuth();

  const { data } = useGetPaymentByIdQuery(paymentId || "");

  const {
    data: registrationData,
    isLoading,
    error,
  } = useGetEventRegistrationQuery(eventRegistrationId);

  const [createSslPayment, { data: sslPaymentData }] =
    useMultipleSslPaymentMutation();

  const {
    member_fee,
    child_fee,
    adult_fee,
    driver_fee,
    guest_fee,
    event_name,
    event_date,
    event_time,
    event_location,
    last_payment_date,
  } = eventData || {};

  const getProductName = () => {
    switch (data?.payment_category) {
      case PAYMENT_CATEGORIES.ADDITIONAL_COST:
        return data?.description || "Additional Due";
      case PAYMENT_CATEGORIES.EVENT_REGISTRATION:
        return data?.description || "Event Registration Fee";
      case PAYMENT_CATEGORIES.MEMBER_FEE:
      default:
        return "Membership Payment";
    }
  };

  const handleSslPayment = async (transaction_id: string) => {
    try {
      await createSslPayment({
        // totalAmount: parseFloat(data?.amount || "0"),
        // currency: "BDT",
        // paymentIds: transaction_id,
        // productName: getProductName(),
        // productCategory: data?.payment_category || "Membership",
        // productProfile: "general",
        // cusName: "Member",
        // cusEmail: "member@example.com",
        // cusPhone: "01700000000",
        // cusAdd1: "Dhaka",
        // cusCity: "Dhaka",
        // cusPostcode: "1000",
        // cusCountry: "Bangladesh",
        // shippingMethod: "NO",
        // numOfItem: 1,
        paymentIds: [paymentId],
        currency: "BDT",
        productName: "string",
        productCategory: "string",
        productProfile: "general",
        cusName: "string",
        cusEmail: "string",
        cusPhone: "string",
        cusAdd1: "string",
        cusCity: "string",
        cusCountry: "string",
        cusPostcode: "string",
        shippingMethod: "NO",
      }).unwrap();
    } catch (err: any) {
      console.error("SSL Commerce payment error:", err);
      // Global error handler will show the toast
    }
  };

  const handleNext = () => {
    if (value === "online") {
      // navigate(
      //   `/${roles[0]?.toLocaleLowerCase()}/membership-fee/online/${paymentId}`
      // );
      handleSslPayment(data.transaction_id);
    } else if (value === "offline") {
      // Handle offline payment logic here
      navigate(
        `/${roles[0]?.toLocaleLowerCase()}/membership-fee/offline/${paymentId}`,
      );
      setValue(null);
    }
  };

  const onlinePay = () => {
    handleSslPayment(data.transaction_id);
  };

  const offlinePay = () => {
    navigate(
      `/${roles[0]?.toLocaleLowerCase()}/membership-fee/offline/${paymentId}`,
    );
    setValue(null);
  };

  useEffect(() => {
    if (value) {
      handleNext();
    }
  }, [value]);

  useEffect(() => {
    if (sslPaymentData) {
      setValue(null);
      window.location.replace(sslPaymentData?.sslUrl);
    }
  }, [sslPaymentData]);

  return (
    <Dialog.Root
      placement={"center"}
      open={open}
      onOpenChange={(details) => setOpen(details.open)}
      size={"md"}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content maxH={"90vh"} overflow={"auto"}>
            <Dialog.Header alignItems={"center"} w={"full"}>
              <Dialog.Title textStyle={"xl"}>Payment Information</Dialog.Title>
            </Dialog.Header>
            <Dialog.Body alignItems={"center"} w={"full"}>
              <Card.Root>
                <Card.Body bg={"blue.50"} p={{ base: 2, md: 5 }}>
                  <Heading
                    size="xl"
                    fontWeight={"bold"}
                    mb={2}
                    color="gray.700"
                  >
                    {event_name}
                  </Heading>
                  <HStack gap={6} mb={3} flexWrap="wrap">
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">
                      <Flex align={"center"} gap={1}>
                        <Icon as={TbCalendarTime} /> Event Date:{" "}
                        {event_date || "N/A"}
                      </Flex>
                    </Text>
                    <Text fontSize="sm" fontWeight="medium" color="red.600">
                      <Flex align={"center"} gap={1}>
                        <Icon as={TbCalendarTime} /> Registration End:{" "}
                        {last_payment_date || "N/A"}
                      </Flex>
                    </Text>
                  </HStack>
                  <HStack color="gray.600" fontSize="sm" mb={3}>
                    <Flex gap={1} align={"center"}>
                      <LuMapPin size={16} />
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">
                        {event_location || "N/A"}
                      </Text>
                    </Flex>
                    {event_time && (
                      <Flex align={"center"} gap={1}>
                        <LuClock size={16} style={{ marginLeft: "5px" }} />
                        <Text fontSize="sm" fontWeight="medium">
                          {formatTime(event_time)}
                        </Text>
                      </Flex>
                    )}
                  </HStack>
                </Card.Body>
              </Card.Root>
              <VStack gap={3} my={3} align="stretch" w={"full"}>
                <Table.ScrollArea
                  borderWidth="1px"
                  borderRadius={"md"}
                  maxW={{ base: "350px", md: "full" }}
                >
                  <Table.Root borderColor="gray.200">
                    <Table.Body>
                      <Table.Row>
                        <Table.Cell>
                          {registrationData?.member?.full_name}
                        </Table.Cell>
                        <Table.Cell textAlign="end">
                          {eventData?.member_fee || "0"}
                        </Table.Cell>
                      </Table.Row>
                    </Table.Body>
                  </Table.Root>
                </Table.ScrollArea>
              </VStack>
              {registrationData?.family_members?.length > 0 && (
                <VStack my={3} gap={3} align="stretch" w={"full"}>
                  <Heading size="sm" color="gray.700">
                    Family Members
                  </Heading>
                  <Table.ScrollArea
                    borderWidth="1px"
                    borderRadius={"md"}
                    maxW={{ base: "350px", md: "full" }}
                  >
                    <Table.Root borderColor="gray.200">
                      <Table.Body>
                        {registrationData?.family_members?.map(
                          (member, index) => (
                            <Table.Row key={member?.id}>
                              <Table.Cell>{member?.name}</Table.Cell>
                              <Table.Cell textAlign="end">
                                {getFee(
                                  eventData,
                                  calculateAge(member?.date_of_birth),
                                  member?.relationship,
                                ).toFixed(2)}
                              </Table.Cell>
                            </Table.Row>
                          ),
                        )}
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                </VStack>
              )}

              {registrationData?.guests?.length > 0 && (
                <VStack gap={3} my={3} align="stretch" w={"full"}>
                  <Heading size="sm" color="gray.700">
                    Guests
                  </Heading>
                  <Table.ScrollArea
                    borderWidth="1px"
                    borderRadius={"md"}
                    maxW={{ base: "350px", md: "full" }}
                  >
                    <Table.Root borderColor="gray.200">
                      <Table.Body>
                        {registrationData?.guests?.map((member, index) => (
                          <Table.Row key={member?.id}>
                            <Table.Cell>{member?.full_name}</Table.Cell>
                            <Table.Cell textAlign="end">
                              {eventData?.guest_fee || "0"}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                </VStack>
              )}
              {registrationData?.drivers?.length > 0 && (
                <VStack gap={3} my={3} align="stretch" w={"full"}>
                  <Heading size="sm" color="gray.700">
                    Drivers
                  </Heading>
                  <Table.ScrollArea
                    borderWidth="1px"
                    borderRadius={"md"}
                    maxW={{ base: "350px", md: "full" }}
                  >
                    <Table.Root borderColor="gray.200">
                      <Table.Body>
                        {registrationData?.drivers?.map((member, index) => (
                          <Table.Row key={member?.id}>
                            <Table.Cell>{member?.full_name}</Table.Cell>

                            <Table.Cell textAlign="end">
                              {eventData?.driver_fee || "0"}
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Table.ScrollArea>
                </VStack>
              )}
              <HStack justifyContent={"space-between"} w={"full"} mt={4}>
                <Text fontSize="lg" fontWeight="bold" color="gray.700">
                  Total Amount:
                </Text>
                <Text fontSize="lg" fontWeight="bold" color="gray.700">
                  {registrationData?.payment?.amount || "0.00"}
                </Text>
              </HStack>
            </Dialog.Body>
            <Dialog.Footer display="flex" justifyContent="center">
              <Center gap={3} mt={6}>
                <Button
                  bg="blue.800"
                  borderRadius={"full"}
                  px={8}
                  onClick={onlinePay}
                >
                  Online Pay
                </Button>
                <Button
                  variant={"outline"}
                  colorPalette={"blue"}
                  borderRadius={"full"}
                  onClick={offlinePay}
                  px={8}
                >
                  Offline Pay
                </Button>
              </Center>
            </Dialog.Footer>
            <Dialog.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Dialog.CloseTrigger>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

const items = [
  { label: "Online", value: "online" },
  { label: "Offline", value: "offline" },
];

export default EventRegistrationPaymentDialog;
